/* eslint-disable relay/unused-fields -- customTime is consumed by the shared sortLiveContentsByCustomTime helper */
import type { FunctionComponentWithRef } from "react";
import { Fragment, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { theme } from "shared/lib/styles";

import type { DefaultComponentsProvider } from "scmp-app/components/article/article-render";
import { sortLiveContentsByCustomTime } from "scmp-app/components/article/article-render/live-article/helpers";
import type { contentLiveArticleArticle$key } from "scmp-app/queries/__generated__/contentLiveArticleArticle.graphql";
import type { contentLiveArticleLiveArticle$key } from "scmp-app/queries/__generated__/contentLiveArticleLiveArticle.graphql";

import { AdSlotContainer, Container, StyledLiveArticleContentItem } from "./styles";

type Props = {
  articleReference: contentLiveArticleArticle$key;
  className?: string;
  deepLinkPosition?: number;
  isTheEnd?: boolean;
  liveArticleReference: contentLiveArticleLiveArticle$key;
  newUpdateCount?: number;
  provider: DefaultComponentsProvider;
};

export const LiveArticleContent: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  articleReference,
  className,
  deepLinkPosition = 0,
  isTheEnd = false,
  liveArticleReference,
  provider,
  ref,
}) => {
  const article = useFragment(
    graphql`
      fragment contentLiveArticleArticle on Article {
        ...itemLiveArticleContentArticle
      }
    `,
    articleReference,
  );

  const contents = useFragment(
    graphql`
      fragment contentLiveArticleLiveArticle on LiveArticle {
        liveContents {
          customTime
          entityUuid
          ...itemLiveArticleContentLiveContent
        }
      }
    `,
    liveArticleReference,
  );

  const liveContents = useMemo(
    () => sortLiveContentsByCustomTime(contents?.liveContents),
    [contents],
  );

  const renderLiveContents = useMemo(
    () => liveContents.slice(deepLinkPosition),
    [deepLinkPosition, liveContents],
  );

  return (
    <Container className={className} ref={ref}>
      {renderLiveContents.map((liveContent, index) => (
        <Fragment key={liveContent.entityUuid ?? index}>
          <StyledLiveArticleContentItem
            articleReference={article}
            isTheEnd={index === 0 && isTheEnd}
            liveContentReference={liveContent}
            provider={provider}
          />
          {index === 3 && (
            <AdSlotContainer>
              {provider.adslot({
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                sizes: [
                  [300, 250],
                  [300, 600],
                  [320, 480],
                ],
                withBackground: false,
                withLabel: true,
              })}
            </AdSlotContainer>
          )}
          {index === 13 && (
            <AdSlotContainer>
              {provider.adslot({
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                sizes: [
                  [300, 250],
                  [300, 600],
                  [320, 480],
                ],
                withBackground: false,
                withLabel: true,
              })}
            </AdSlotContainer>
          )}
        </Fragment>
      ))}
    </Container>
  );
};

LiveArticleContent.displayName = "LiveArticleContent";
